import React, { useEffect, useState } from "react";
import techniciansService from "../../../api/techniciansService";
import { getDefaultEvaluationPeriod } from "../../../utils/evaluationPeriod";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const YEARS = [2026, 2025, 2024];

/**
 * Columnas de puntaje del ranking. El campo `weight` es solo informativo
 * (el cálculo ponderado se hace en el servidor).
 */
const SCORE_COLUMNS = [
  { key: "serviceQuality", label: "Calidad", weight: 25, color: "#6366f1" },
  { key: "punctuality", label: "Puntualidad", weight: 20, color: "#0ea5e9" },
  { key: "attitude", label: "Actitud", weight: 20, color: "#10b981" },
  { key: "reportCompliance", label: "Reportes", weight: 20, color: "#f59e0b" },
  { key: "adminCompliance", label: "Cumpl. Admin", weight: 15, color: "#8b5cf6" },
];

function scoreMeta(score) {
  if (score >= 85) return { fill: "#22c55e", track: "rgba(34,197,94,0.12)", text: "#16a34a" };
  if (score >= 70) return { fill: "#84cc16", track: "rgba(132,204,22,0.12)", text: "#65a30d" };
  if (score >= 50) return { fill: "#f59e0b", track: "rgba(245,158,11,0.12)", text: "#d97706" };
  if (score > 0)   return { fill: "#ef4444", track: "rgba(239,68,68,0.12)", text: "#dc2626" };
  return { fill: "#cbd5e1", track: "rgba(148,163,184,0.12)", text: "#94a3b8" };
}

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .trk-root {
    --font: 'DM Sans', system-ui, sans-serif;
    --font-mono: 'DM Mono', monospace;
    --border: #e4e7ed;
    --text-primary: #0f1117;
    --text-secondary: #5a6175;
    --text-tertiary: #9ba3b5;
    font-family: var(--font);
  }
  .trk-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .trk-scroll::-webkit-scrollbar-track { background: transparent; margin: 4px; }
  .trk-scroll::-webkit-scrollbar-thumb { background: #d1d5e0; border-radius: 100px; }
  .trk-scroll::-webkit-scrollbar-thumb:hover { background: #9ba3b5; }
  .trk-scroll { scrollbar-width: thin; scrollbar-color: #d1d5e0 transparent; }

  .trk-select {
    appearance: none; -webkit-appearance: none;
    background: #fff; border: 1.5px solid var(--border); border-radius: 10px;
    color: var(--text-primary); font-size: 13px; font-weight: 500; font-family: var(--font);
    padding: 7px 30px 7px 12px; cursor: pointer; outline: none; line-height: 1.4;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .trk-select:hover { border-color: #c4c9d8; }
  .trk-select:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }

  .trk-row { transition: background 0.12s; }
  .trk-row:hover td { background: rgba(99,102,241,0.025) !important; }
  .trk-row:hover td:first-child { background: rgba(99,102,241,0.04) !important; }
  .trk-winner td { background: rgba(245,158,11,0.06) !important; }
  .trk-winner:hover td { background: rgba(245,158,11,0.10) !important; }

  @keyframes trk-spin { to { transform: rotate(360deg); } }
  @keyframes trk-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
  .trk-animate { animation: trk-fade-in 0.3s ease forwards; }

  .trk-bar-fill { height: 100%; border-radius: 100px; transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1); }

  .trk-th {
    position: sticky; top: 0; z-index: 10; background: #f8f9fc;
    font-size: 10.5px; font-weight: 600; letter-spacing: 0.05em;
    text-transform: uppercase; color: var(--text-tertiary);
  }
  .trk-tfoot td { background: #f8f9fc; border-top: 1.5px solid var(--border); }
  .trk-count { font-family: var(--font-mono); font-size: 13px; font-weight: 500; }
  .trk-mono { font-family: var(--font-mono); }
`;

function ScoreCell({ value }) {
  const m = scoreMeta(value);
  return (
    <td style={{ padding: "9px 8px", borderBottom: "1px solid #f0f2f6", verticalAlign: "middle" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
          <span className="trk-mono" style={{ fontSize: "12px", fontWeight: 600, color: m.text }}>
            {value}
          </span>
        </div>
        <div style={{ height: "4px", background: m.track, borderRadius: "100px", overflow: "hidden" }}>
          <div className="trk-bar-fill" style={{ width: `${value}%`, background: m.fill }} />
        </div>
      </div>
    </td>
  );
}

export default function TechnicianRankingTable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const defaultPeriod = getDefaultEvaluationPeriod();
  const [selectedYear, setSelectedYear] = useState(defaultPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(defaultPeriod.month);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await techniciansService.getRanking(selectedYear, selectedMonth);
        if (active) setData(res.data.data || res.data);
      } catch (err) {
        console.error("Error cargando ranking", err);
        if (active) setError("No se pudo cargar el ranking");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [selectedYear, selectedMonth]);

  const ranking = (data?.ranking || []).filter(
    (r) => r.status !== "inactive"
  );
  const teamAverages = data?.teamAverages || {};
  const employeeOfMonth = data?.employeeOfMonth || null;

  const TH = { className: "trk-th", style: { padding: "10px 8px", borderBottom: "1.5px solid #e4e7ed" } };

  return (
    <div className="trk-root" style={{
      display: "flex", flexDirection: "column", flex: 1, width: "100%", height: "100%",
      minHeight: 0, background: "#ffffff",
      borderRadius: "12px", border: "1.5px solid #e4e7ed", overflow: "hidden",
      fontSize: "13px", color: "#0f1117",
      boxShadow: "0 2px 12px rgba(15,17,23,0.05)",
    }}>
      <style>{GLOBAL_STYLES}</style>

      {/* ══ HEADER ══ */}
      <div style={{
        padding: "10px 16px", borderBottom: "1.5px solid #f0f2f6", flexShrink: 0,
        background: "linear-gradient(180deg, #fafbfd 0%, #ffffff 100%)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f1117", letterSpacing: "-0.01em" }}>
              Ranking de Técnicos · Empleado del Mes
            </div>
            <div style={{ fontSize: "12px", color: "#9ba3b5", marginTop: "3px" }}>
              {!loading
                ? `${ranking.length} técnicos · ${MONTHS[selectedMonth - 1]} ${selectedYear} · Período del mes anterior`
                : "Cargando datos…"}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            {[
              { value: selectedMonth, onChange: setSelectedMonth, options: MONTHS.map((m, i) => ({ label: m, value: i + 1 })) },
              { value: selectedYear, onChange: setSelectedYear, options: YEARS.map((y) => ({ label: String(y), value: y })) },
            ].map((sel, idx) => (
              <div key={idx} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                <select className="trk-select" value={sel.value} onChange={(e) => sel.onChange(Number(e.target.value))}>
                  {sel.options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </select>
                <svg style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                  width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="#9ba3b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ BANNER EMPLEADO DEL MES ══ */}
      {!loading && employeeOfMonth && (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px", padding: "8px 16px",
          borderBottom: "1.5px solid #f0f2f6",
          background: "linear-gradient(90deg, rgba(245,158,11,0.12), rgba(245,158,11,0.02))",
        }}>
          <span style={{ fontSize: "20px", lineHeight: 1 }} role="img" aria-label="trofeo">🏆</span>
          <div>
            <div style={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#d97706" }}>
              Empleado del Mes
            </div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#0f1117" }}>
              {employeeOfMonth.technicianName}
              <span className="trk-mono" style={{ marginLeft: "8px", fontSize: "13px", color: "#d97706" }}>
                {employeeOfMonth.total} pts
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ══ TABLA ══ */}
      <div className="trk-scroll" style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "220px", gap: "12px", color: "#9ba3b5" }}>
            <svg width="20" height="20" viewBox="0 0 20 20" style={{ animation: "trk-spin 0.8s linear infinite" }}>
              <circle cx="10" cy="10" r="8" fill="none" stroke="#e4e7ed" strokeWidth="2.5" />
              <path d="M10 2a8 8 0 0 1 8 8" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: "13px", fontWeight: 500 }}>Cargando ranking…</span>
          </div>
        ) : error ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#dc2626", fontSize: "13px" }}>{error}</div>
        ) : ranking.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#9ba3b5", fontSize: "13px" }}>
            No hay técnicos para mostrar.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: "820px" }}>
            <thead>
              <tr>
                <th {...TH} style={{ ...TH.style, padding: "10px 12px 10px 20px", textAlign: "left", width: "22%" }}>
                  # · Técnico
                </th>
                {SCORE_COLUMNS.map((col) => (
                  <th key={col.key} {...TH} style={{ ...TH.style, textAlign: "center", borderBottom: `2px solid ${col.color}`, color: col.color }}>
                    <div>{col.label}</div>
                    <div style={{ fontSize: "9px", fontWeight: 500, color: "#9ba3b5", marginTop: "2px" }}>{col.weight}%</div>
                  </th>
                ))}
                <th {...TH} style={{ ...TH.style, padding: "10px 20px 10px 8px", textAlign: "right", width: "13%" }}>
                  <div>Total</div>
                  <div style={{ fontSize: "9px", fontWeight: 500, color: "#9ba3b5", marginTop: "2px" }}>100%</div>
                </th>
              </tr>
            </thead>

            <tbody>
              {ranking.map((tech, idx) => {
                const m = scoreMeta(tech.total);
                return (
                  <tr key={tech.technicianId} className={`trk-row trk-animate ${tech.isEmployeeOfMonth ? "trk-winner" : ""}`} style={{ animationDelay: `${idx * 30}ms` }}>
                    {/* Posición + nombre */}
                    <td style={{ padding: "9px 12px 9px 20px", borderBottom: "1px solid #f0f2f6", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span className="trk-mono" style={{
                          fontSize: "12px", fontWeight: 600, minWidth: "20px", textAlign: "center",
                          color: idx === 0 ? "#d97706" : "#9ba3b5",
                        }}>
                          {tech.isEmployeeOfMonth ? "🏆" : idx + 1}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "#0f1117", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px" }}>
                            {tech.technicianName}
                          </div>
                          <div style={{ fontSize: "10.5px", color: "#9ba3b5" }}>
                            {tech.servicesWithReport}/{tech.totalServices} reportes
                            {!tech.hasEvaluation && (
                              <span style={{ marginLeft: 6, color: "#f59e0b" }}>· sin calif. Calidad</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {SCORE_COLUMNS.map((col) => (
                      <ScoreCell key={col.key} value={tech.scores?.[col.key] ?? 0} />
                    ))}

                    {/* Total */}
                    <td style={{ padding: "9px 20px 9px 8px", borderBottom: "1px solid #f0f2f6", textAlign: "right", verticalAlign: "middle" }}>
                      <span style={{
                        display: "inline-block", padding: "4px 10px", borderRadius: "100px",
                        background: m.track, color: m.text, fontWeight: 700, fontSize: "13px",
                        fontFamily: "'DM Mono', monospace",
                      }}>
                        {tech.total}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* ══ FOOTER: promedios del equipo ══ */}
            <tfoot>
              <tr className="trk-tfoot">
                <td style={{ padding: "10px 12px 10px 20px" }}>
                  <span style={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5a6175" }}>
                    Promedio equipo
                  </span>
                </td>
                {SCORE_COLUMNS.map((col) => (
                  <td key={col.key} style={{ padding: "10px 8px", textAlign: "center" }}>
                    <span className="trk-count" style={{ fontWeight: 600, color: col.color }}>
                      {teamAverages?.[col.key] ?? 0}
                    </span>
                  </td>
                ))}
                <td style={{ padding: "10px 20px 10px 8px", textAlign: "right" }}>
                  <span className="trk-count" style={{ fontWeight: 700, color: "#0f1117", fontSize: "14px" }}>
                    {teamAverages?.total ?? 0}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* ══ NOTA DE PESOS ══ */}
      <div style={{ padding: "6px 16px", borderTop: "1.5px solid #f0f2f6", background: "#fafbfd", fontSize: "10px", color: "#9ba3b5", flexShrink: 0 }}>
        Pesos: Calidad 25% · Puntualidad 20% · Actitud 20% · Reportes 20% · Cumpl. Admin 15%
      </div>
    </div>
  );
}
